// deviceProfile.js
// Single source of truth for runtime device-tier detection. Used to gate
// memory-/CPU-heavy work on weaker mobile devices WITHOUT changing UI,
// quality, or behaviour for desktop. Desktop always returns the same
// "high" profile and bypasses every mobile mitigation.
//
// Tier breakdown:
//   high  — desktop, modern iPhone (>=14), Android with >=6 GB RAM
//   mid   — iPhone 12-13, Android 4 GB RAM
//   low   — iPhone < 12 (3-4 GB RAM), Android < 4 GB RAM, low-end tablets
//
// Detection inputs:
//   - userAgent (parsed for OS + iOS major version)
//   - navigator.deviceMemory (Android Chrome / Edge — not available on iOS)
//   - navigator.hardwareConcurrency
//   - navigator.connection.effectiveType (network class — affects boot pace)
//
// The result is computed once on first import and frozen.

const _ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';

const _isMobileUA =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(_ua);

const _isIOS = /iPad|iPhone|iPod/.test(_ua) && !(typeof window !== 'undefined' && window.MSStream);

const _isAndroid = /Android/i.test(_ua);

// iOS major version (e.g. "16" from "iPhone OS 16_4_1"). Returns 0 if unknown.
const _iosMajor = (() => {
  if (!_isIOS) return 0;
  const m = _ua.match(/OS (\d+)[._]/);
  return m ? parseInt(m[1], 10) : 0;
})();

// Heuristic iPhone-model class. iOS Safari hides exact model, so we use
// screen size + iOS version + DPR to approximate. We err on the side of
// "low" — false positives just defer non-critical work, never break it.
const _iPhoneClass = (() => {
  if (!_isIOS) return null;
  if (typeof window === 'undefined') return 'mid';
  const w = Math.max(window.screen?.width || 0, window.screen?.height || 0);
  const dpr = window.devicePixelRatio || 1;
  // iPhone 14+ baseline: 844 logical px tall AND iOS >= 16
  if (w >= 844 && _iosMajor >= 16 && dpr >= 3) return 'high';
  // iPhone 12-13: 844 px tall, iOS 14+
  if (w >= 844 && _iosMajor >= 14) return 'mid';
  // Older iPhones (SE, 8, X, XR, 11) — small screens, 3 GB RAM
  return 'low';
})();

const _deviceMemory =
  (typeof navigator !== 'undefined' && typeof navigator.deviceMemory === 'number')
    ? navigator.deviceMemory
    : null;

const _hardwareConcurrency =
  (typeof navigator !== 'undefined' && typeof navigator.hardwareConcurrency === 'number')
    ? navigator.hardwareConcurrency
    : null;

const _effectiveType = (() => {
  try {
    return navigator.connection?.effectiveType || null;
  } catch (_) {
    return null;
  }
})();

function _computeTier() {
  if (!_isMobileUA) return 'high';            // desktop/laptop — no mitigations
  if (_isIOS) return _iPhoneClass || 'mid';
  // Android & other mobile
  if (_deviceMemory != null) {
    if (_deviceMemory >= 6) return 'high';
    if (_deviceMemory >= 4) return 'mid';
    return 'low';
  }
  // No deviceMemory — fall back on cores (low-end Androids have <=4 cores)
  if (_hardwareConcurrency != null) {
    if (_hardwareConcurrency >= 8) return 'high';
    if (_hardwareConcurrency >= 6) return 'mid';
  }
  // Slow network often correlates with weak hardware in emerging markets
  if (_effectiveType && /^(slow-2g|2g|3g)$/.test(_effectiveType)) return 'low';
  return 'mid';
}

const tier = _computeTier();

const profile = Object.freeze({
  // Booleans
  isMobile: _isMobileUA,
  isIOS: _isIOS,
  isAndroid: _isAndroid,

  // Tier helpers
  tier,                                    // 'high' | 'mid' | 'low'
  isLowTier: tier === 'low',
  isMidTier: tier === 'mid',
  isHighTier: tier === 'high',
  isWeakDevice: tier !== 'high',           // mid OR low — apply mobile mitigations

  // Diagnostics (read-only)
  iosMajor: _iosMajor,
  deviceMemory: _deviceMemory,
  hardwareConcurrency: _hardwareConcurrency,
  effectiveType: _effectiveType,
});

// Expose on window for ad-hoc debugging in DevTools console.
if (typeof window !== 'undefined') {
  try { window._deviceProfile = profile; } catch (_) { /* ignore */ }
}

export default profile;
